import { forwardRef } from "react";
import { Calendar, ShieldCheck, Building2 } from "lucide-react";
import { fileUrl } from "@/lib/api";

const NAVY = "#0B1F44";
const GOLD = "#C6A052";
const GOLD_D = "#9A7B33";
const GOLD_L = "#E7CE8E";
const CREAM = "#FCFAF5";
const SITE_LOGO = "/logo-full.png";
const SITE_SEAL = "/logo-mark.png";

function fmtDate(iso) {
  if (!iso) return new Date().toLocaleDateString("fr-FR");
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
}

function Corner({ pos }) {
  const big = pos === "bl" || pos === "br";
  const size = big ? 250 : 172;
  const flip = { tl: "none", tr: "scaleX(-1)", bl: "scaleY(-1)", br: "scale(-1,-1)" }[pos];
  const style = {
    position: "absolute", width: size, height: size, zIndex: 3,
    top: pos[0] === "t" ? 0 : "auto", bottom: pos[0] === "b" ? 0 : "auto",
    left: pos[1] === "l" ? 0 : "auto", right: pos[1] === "r" ? 0 : "auto",
    transform: flip, transformOrigin: "center",
  };
  return (
    <svg style={style} viewBox="0 0 200 200" fill="none">
      <path d="M0 0 L200 0 L0 200 Z" fill={NAVY} />
      <path d="M191 0 L0 191" stroke={GOLD} strokeWidth="3.5" />
      <path d="M150 0 L0 150" stroke={GOLD_L} strokeWidth="1.6" />
      <path d="M118 0 L0 118" stroke={GOLD} strokeWidth="1" opacity="0.7" />
      <path d="M0 0 L46 0 L0 46 Z" fill={GOLD} />
    </svg>
  );
}

function Star({ x, y, r = 5, fill = GOLD }) {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r / 2.4;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${x + rad * Math.cos(a)},${y + rad * Math.sin(a)}`);
  }
  return <polygon points={pts.join(" ")} fill={fill} />;
}

function BravoBadge() {
  return (
    <svg width="152" height="188" viewBox="0 0 152 188"
      style={{ position: "absolute", top: 20, right: 58, zIndex: 5 }} fill="none">
      {/* ribbon tails */}
      <path d="M52 118 L52 182 L76 166 L100 182 L100 118 Z" fill={NAVY} />
      <path d="M52 118 L52 182 L64 174 L64 118 Z" fill={GOLD_D} opacity="0.55" />
      <path d="M100 118 L100 182 L88 174 L88 118 Z" fill={GOLD_D} opacity="0.55" />
      {/* scalloped outer ring */}
      <circle cx="76" cy="70" r="66" fill={GOLD} />
      <circle cx="76" cy="70" r="60" fill={NAVY} />
      <circle cx="76" cy="70" r="60" fill="none" stroke={GOLD_L} strokeWidth="1.4" strokeDasharray="3 4" />
      <circle cx="76" cy="70" r="49" fill="none" stroke={GOLD} strokeWidth="2.4" />
      {/* stars */}
      <Star x={54} y={40} r={5} /><Star x={76} y={34} r={6} /><Star x={98} y={40} r={5} />
      <Star x={58} y={104} r={4} /><Star x={76} y={108} r={5} /><Star x={94} y={104} r={4} />
      {/* text */}
      <text x="76" y="58" textAnchor="middle" fill={GOLD_L}
        style={{ fontFamily: "Cinzel, serif", fontSize: 8.5, letterSpacing: 1.2, fontWeight: 700 }}>
        FÉLICITATIONS
      </text>
      <rect x="30" y="63" width="92" height="20" rx="3" fill={GOLD} />
      <text x="76" y="78" textAnchor="middle" fill={NAVY}
        style={{ fontFamily: "Cinzel, serif", fontSize: 15, letterSpacing: 1.5, fontWeight: 800 }}>
        BRAVO !
      </text>
    </svg>
  );
}

function Medal() {
  return (
    <svg width="72" height="96" viewBox="0 0 72 96" fill="none"
      style={{ position: "absolute", left: "50%", bottom: 20, transform: "translateX(-50%)", zIndex: 4 }}>
      <path d="M22 40 L22 92 L36 80 L50 92 L50 40 Z" fill={NAVY} />
      <circle cx="36" cy="34" r="30" fill={GOLD} />
      <circle cx="36" cy="34" r="24" fill={NAVY} />
      <circle cx="36" cy="34" r="24" fill="none" stroke={GOLD_L} strokeWidth="1.2" strokeDasharray="2 3" />
      <Star x={36} y={34} r={12} fill={GOLD} />
    </svg>
  );
}

function InfoCol({ icon: Icon, label, value }) {
  return (
    <div style={{ flex: 1, textAlign: "center", padding: "0 10px" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 34, height: 34, borderRadius: 999, background: "rgba(11,31,68,0.06)",
        }}>
          <Icon size={18} color={NAVY} />
        </span>
      </div>
      <div style={{
        fontFamily: "Cinzel, serif", fontSize: 10, letterSpacing: 1.4, fontWeight: 700,
        color: GOLD_D, textTransform: "uppercase",
      }}>{label}</div>
      <div style={{
        marginTop: 3, fontFamily: "Cormorant Garamond, serif", fontSize: 16,
        fontWeight: 600, color: NAVY,
      }}>{value}</div>
    </div>
  );
}

/**
 * Certificat de réussite ISMO-TECH — rendu fidèle (web + capture PDF).
 * data: { certificate_number, user_name, formation_title, issued_at,
 *         issuer_name, issuer_logo, issuer_signature, issuer_stamp,
 *         signatory_name, signatory_title }
 */
export const Certificate = forwardRef(function Certificate({ data = {} }, ref) {
  const issuerType = data.issuer_type || "admin";
  const logo = data.issuer_logo ? fileUrl(data.issuer_logo) : (issuerType === "admin" ? SITE_LOGO : "");
  const signature = data.issuer_signature ? fileUrl(data.issuer_signature) : "";
  const stamp = data.issuer_stamp ? fileUrl(data.issuer_stamp) : "";
  const issuerName = data.issuer_name || "ISMO-TECH";
  const signName = data.signatory_name || issuerName;
  const signTitle = data.signatory_title || "Directeur Général";

  return (
    <div ref={ref} id="cert-capture" style={{
      position: "relative", width: 1000, height: 707, background: CREAM,
      overflow: "hidden", fontFamily: "Cormorant Garamond, Georgia, serif",
      boxShadow: "0 30px 80px -20px rgba(11,31,68,0.35)",
    }}>
      {/* subtle paper texture */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 32%, rgba(198,160,82,0.05), transparent 60%)",
      }} />
      {/* big discreet ISMO watermark = preuve d'origine */}
      <img src={SITE_SEAL} alt="" style={{
        position: "absolute", left: "50%", top: "52%", width: 420,
        transform: "translate(-50%,-50%)", opacity: 0.045, zIndex: 1,
      }} />

      {/* gold double frame */}
      <div style={{ position: "absolute", inset: 16, border: `3px solid ${GOLD}`, zIndex: 2 }} />
      <div style={{ position: "absolute", inset: 24, border: `1px solid ${GOLD}`, opacity: 0.7, zIndex: 2 }} />

      <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
      <BravoBadge />

      {/* content */}
      <div style={{ position: "relative", zIndex: 4, padding: "44px 90px 0", textAlign: "center" }}>
        {logo ? (
          <img src={logo} alt={issuerName} crossOrigin="anonymous"
            style={{ height: 58, objectFit: "contain", margin: "0 auto 6px" }} />
        ) : (
          <div style={{
            height: 58, display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "Cinzel, serif", fontSize: 26, fontWeight: 800, color: NAVY, letterSpacing: 2,
          }}>{issuerName}</div>
        )}

        <div style={{
          fontFamily: "Cinzel, serif", fontSize: 62, lineHeight: 1, fontWeight: 800,
          color: NAVY, letterSpacing: 3, marginTop: 8,
        }}>CERTIFICAT</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginTop: 6 }}>
          <span style={{ width: 60, height: 2, background: GOLD }} />
          <span style={{
            fontFamily: "Cinzel, serif", fontSize: 20, fontWeight: 600,
            color: GOLD_D, letterSpacing: 5,
          }}>DE RÉUSSITE</span>
          <span style={{ width: 60, height: 2, background: GOLD }} />
        </div>

        <div style={{ marginTop: 22, fontSize: 18, color: "#5A6474", fontStyle: "italic" }}>
          Ce certificat est décerné à
        </div>
        <div style={{
          fontFamily: "'Great Vibes', cursive", fontSize: 62, lineHeight: 1.1,
          color: GOLD_D, marginTop: 2,
        }}>{data.user_name || "—"}</div>

        {/* flourish */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, margin: "2px 0 10px" }}>
          <span style={{ width: 130, height: 1, background: GOLD, opacity: 0.6 }} />
          <span style={{ color: GOLD, fontSize: 12 }}>❖</span>
          <span style={{ width: 130, height: 1, background: GOLD, opacity: 0.6 }} />
        </div>

        <div style={{ fontSize: 17, color: "#5A6474" }}>
          pour avoir terminé avec succès la formation
        </div>
        <div style={{
          display: "inline-block", marginTop: 12, padding: "9px 30px", background: NAVY,
          borderRadius: 6, border: `1.5px solid ${GOLD}`,
          fontFamily: "Cinzel, serif", fontSize: 18, fontWeight: 700, color: "#fff", letterSpacing: 0.5,
        }}>« {data.formation_title || ""} »</div>
      </div>

      {/* info row */}
      <div style={{
        position: "absolute", left: 92, right: 92, top: 470, zIndex: 4,
        display: "flex", alignItems: "flex-start",
      }}>
        <InfoCol icon={Calendar} label="Date de délivrance" value={fmtDate(data.issued_at)} />
        <span style={{ width: 1, height: 46, background: "rgba(11,31,68,0.12)" }} />
        <InfoCol icon={ShieldCheck} label="N° de certificat" value={data.certificate_number || "—"} />
        <span style={{ width: 1, height: 46, background: "rgba(11,31,68,0.12)" }} />
        <InfoCol icon={Building2} label={issuerName} value="Organisme de formation" />
      </div>

      <div style={{
        position: "absolute", left: 0, right: 0, top: 566, zIndex: 4, textAlign: "center",
        fontSize: 12.5, color: "#8A93A3", padding: "0 120px",
      }}>
        Vérifiez l'authenticité de ce certificat auprès de ISMO-TECH avec son numéro unique.
      </div>

      {/* signature bottom-left */}
      <div style={{ position: "absolute", left: 96, bottom: 40, width: 240, textAlign: "center", zIndex: 4 }}>
        <div style={{ height: 46, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          {signature
            ? <img src={signature} alt="" crossOrigin="anonymous" style={{ maxHeight: 46, maxWidth: 200, objectFit: "contain" }} />
            : <span style={{ fontFamily: "'Great Vibes', cursive", fontSize: 30, color: NAVY }}>{signName}</span>}
        </div>
        <div style={{ borderTop: `1.4px solid ${NAVY}`, marginTop: 4, paddingTop: 5 }}>
          <div style={{ fontFamily: "Cinzel, serif", fontSize: 12, fontWeight: 700, color: NAVY, letterSpacing: 0.5 }}>{signName}</div>
          <div style={{ fontSize: 12.5, color: "#8A93A3" }}>{signTitle}</div>
        </div>
      </div>

      <Medal />

      {/* issuer stamp bottom-right */}
      <div style={{ position: "absolute", right: 96, bottom: 34, width: 150, height: 118, zIndex: 4 }}>
        {stamp ? (
          <img src={stamp} alt="cachet" crossOrigin="anonymous" style={{
            position: "absolute", right: 14, bottom: 6, width: 104, height: 104,
            objectFit: "contain", transform: "rotate(-9deg)", opacity: 0.92, borderRadius: 999,
          }} />
        ) : (
          <div style={{
            position: "absolute", right: 18, bottom: 8, width: 98, height: 98, borderRadius: 999,
            border: `2px solid ${NAVY}`, transform: "rotate(-9deg)", display: "flex",
            flexDirection: "column", alignItems: "center", justifyContent: "center",
            color: NAVY, textAlign: "center", opacity: 0.85,
          }}>
            <div style={{ fontFamily: "Cinzel, serif", fontSize: 9, fontWeight: 700, letterSpacing: 0.5, padding: "0 6px" }}>
              {issuerName}
            </div>
            <div style={{ width: 46, height: 1, background: NAVY, margin: "3px 0" }} />
            <div style={{ fontSize: 8, letterSpacing: 1 }}>CERTIFIÉ</div>
          </div>
        )}
      </div>

      {/* discreet official ISMO-TECH seal = preuve d'origine */}
      <div style={{
        position: "absolute", right: 30, bottom: 8, zIndex: 5, display: "flex",
        alignItems: "center", gap: 5, opacity: 0.8,
      }}>
        <img src={SITE_SEAL} alt="" style={{ width: 20, height: 20, objectFit: "contain" }} />
        <span style={{ fontFamily: "Cinzel, serif", fontSize: 8.5, color: GOLD_D, letterSpacing: 0.5 }}>
          Vérifié par ISMO-TECH
        </span>
      </div>
    </div>
  );
});

export default Certificate;
